import React, { useEffect, useState } from 'react';
import { FaEnvelope, FaWhatsapp, FaCheckCircle, FaExclamationTriangle, FaRegCircle, FaPaperPlane } from 'react-icons/fa';
import Switch from '../../../../components/Switch';
import DraftBanner from '../../../../components/DraftBanner';
import { supabase } from '../../../../config/supabaseConfig.ts';
import { toastError, toastSuccess } from '../../../../utils/toast';
import { useDraft } from '../../../../hooks/useDraft';

// Canales de envío del proyecto (paso 17). El admin puede configurarlos aquí y el cliente
// en su dashboard → Envíos. La contraseña se manda al guardar y nunca se vuelve a leer.

const C = { line: 'rgba(255,255,255,0.08)', text: '#E8EAF0', muted: '#8A90AD', faint: '#5A6080', input: '#1a1b30' };
const inputCls = 'w-full rounded-xl px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]';
const inputStyle = { backgroundColor: C.input, border: `1px solid ${C.line}`, color: C.text };

const PROVIDERS = {
  gmail: { label: 'Gmail / Workspace', host: 'smtp.gmail.com', port: 587, secure: 'starttls' },
  outlook: { label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: 587, secure: 'starttls' },
  other: { label: 'Otro', host: '', port: 465, secure: 'ssl' },
};

const errMsg = (e) => e?.message?.replace(/^.*?ERROR:\s*/i, '') || 'No se pudo guardar';
const list = (s) => s.split(/[,\s;]+/).map((x) => x.trim()).filter(Boolean);

function Status({ acc }) {
  if (!acc) return <span className="flex items-center gap-1 text-xs" style={{ color: C.muted }}><FaRegCircle aria-hidden /> Sin conectar</span>;
  if (acc.status === 'verified') return <span className="flex items-center gap-1 text-xs" style={{ color: '#4ADE80' }}><FaCheckCircle aria-hidden /> Conectado{acc.enabled ? '' : ' (en pausa)'}</span>;
  if (acc.status === 'error') return <span className="flex items-center gap-1 text-xs" style={{ color: '#F87171' }}><FaExclamationTriangle aria-hidden /> La prueba falló</span>;
  return <span className="flex items-center gap-1 text-xs" style={{ color: '#FBBF24' }}><FaRegCircle aria-hidden /> Falta la prueba</span>;
}

function Input({ id, label, hint, ...props }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm" style={{ color: C.text }}>{label}</label>
      <input id={id} className={inputCls} style={inputStyle} {...props} />
      {hint && <p className="text-xs" style={{ color: C.faint }}>{hint}</p>}
    </div>
  );
}

export default function DeliveryConfig({ projectId }) {
  const [state, setState] = useState(null);
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState(''); // nunca va al borrador
  const [testTo, setTestTo] = useState({ email: '', whatsapp: '' });
  const [busy, setBusy] = useState(null);
  const draft = useDraft(`delivery:${projectId}`, form, setForm, { ready: !!form });

  const fromState = (s) => {
    const em = s.channels.find((c) => c.channel === 'email');
    const wa = s.channels.find((c) => c.channel === 'whatsapp');
    const c = em?.config || {};
    const provider = c.provider || (c.host === PROVIDERS.outlook.host ? 'outlook' : c.host && c.host !== PROVIDERS.gmail.host ? 'other' : 'gmail');
    return {
      email: {
        provider, host: c.host || PROVIDERS[provider].host, port: c.port || PROVIDERS[provider].port,
        secure: c.secure || PROVIDERS[provider].secure, username: c.username || '', from_email: c.from_email || '',
        from_name: c.from_name || '', reply_to: c.reply_to || '', recipients: (em?.recipients || []).join(', '),
        enabled: em?.enabled ?? true, insights: s.insight_channels.includes('email'),
      },
      whatsapp: {
        template_name: wa?.config?.template_name || '', template_lang: wa?.config?.template_lang || 'es_MX',
        recipients: (wa?.recipients || []).join(', '), enabled: wa?.enabled ?? true, insights: s.insight_channels.includes('whatsapp'),
      },
    };
  };

  useEffect(() => {
    if (!projectId) return;
    supabase.rpc('delivery_get', { p_project: projectId }).then(({ data, error }) => {
      if (error) { toastError('No se pudieron cargar los canales de envío (¿corriste 17_delivery_channels.sql?)'); return; }
      setState(data);
      setForm(fromState(data));
    });
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!projectId) return <p className="text-sm" style={{ color: C.muted }}>Guarda el proyecto para configurar sus canales de envío.</p>;
  if (!form) return <p className="text-sm" style={{ color: C.muted }}>Cargando…</p>;

  const em = form.email;
  const wa = form.whatsapp;
  const setEm = (patch) => setForm((f) => ({ ...f, email: { ...f.email, ...patch } }));
  const setWa = (patch) => setForm((f) => ({ ...f, whatsapp: { ...f.whatsapp, ...patch } }));
  const acc = (ch) => state?.channels.find((c) => c.channel === ch);

  const save = async (channel) => {
    setBusy(`save-${channel}`);
    const isEmail = channel === 'email';
    const v = isEmail ? em : wa;
    const { data, error } = await supabase.rpc('delivery_save', {
      p_project: projectId,
      p_channel: channel,
      p_config: isEmail
        ? { provider: em.provider, host: em.host, port: Number(em.port), secure: em.secure, username: em.username || em.from_email, from_email: em.from_email, from_name: em.from_name, reply_to: em.reply_to }
        : { template_name: wa.template_name, template_lang: wa.template_lang },
      p_recipients: list(v.recipients),
      p_secret: isEmail && password.trim() ? password : null,
      p_enabled: v.enabled,
      p_use_for_insights: v.insights,
    });
    setBusy(null);
    if (error) { toastError(errMsg(error)); return false; }
    if (isEmail) setPassword('');
    setState(data);
    draft.clear();
    toastSuccess('Canal guardado');
    return true;
  };

  const test = async (channel) => {
    if (!(await save(channel))) return;
    setBusy(`test-${channel}`);
    const to = testTo[channel] || list((channel === 'email' ? em : wa).recipients)[0] || (channel === 'email' ? em.from_email : '');
    const { data, error } = await supabase.functions.invoke('send-message', { body: { task: 'test', project_id: projectId, channel, to } });
    let result = data;
    if (error) { try { result = await error.context?.json?.(); } catch { result = null; } }
    setBusy(null);
    if (result?.ok) toastSuccess(`Prueba enviada a ${to}`);
    else toastError(result?.error || 'No se pudo enviar la prueba');
    const { data: fresh } = await supabase.rpc('delivery_get', { p_project: projectId });
    if (fresh) setState(fresh);
  };

  const Toggle = ({ id, checked, onChange, children }) => (
    <div className="flex items-start gap-3 py-1">
      <Switch id={id} size="sm" checked={checked} onChange={onChange} label={typeof children === 'string' ? children : id} />
      <label htmlFor={id} className="text-sm cursor-pointer" style={{ color: C.text }}>{children}</label>
    </div>
  );

  const card = { backgroundColor: '#12132a', border: `1px solid ${C.line}` };

  return (
    <div className="space-y-5">
      <DraftBanner restoredAt={draft.restoredAt} onDiscard={draft.discard} />

      <section className="rounded-2xl p-5 space-y-4" style={card} aria-labelledby="dc-email">
        <header className="flex flex-wrap items-center gap-2">
          <FaEnvelope style={{ color: '#00C2FF' }} aria-hidden />
          <h4 id="dc-email" className="text-sm font-semibold flex-1" style={{ color: C.text }}>Correo (SMTP)</h4>
          <Status acc={acc('email')} />
        </header>
        {acc('email')?.status === 'error' && <p className="text-xs" style={{ color: '#F87171' }}>{acc('email').last_error}</p>}
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Proveedor de correo">
          {Object.entries(PROVIDERS).map(([k, p]) => (
            <button key={k} type="button" role="radio" aria-checked={em.provider === k}
              onClick={() => setEm({ provider: k, host: p.host, port: p.port, secure: p.secure })}
              className="rounded-lg px-3 py-1.5 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
              style={em.provider === k ? { backgroundColor: 'rgba(0,194,255,0.12)', color: '#7FDBFF', border: '1px solid rgba(0,194,255,0.4)' } : { color: C.muted, border: `1px solid ${C.line}` }}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input id="dc-from" label="Correo que envía" type="email" value={em.from_email} onChange={(e) => setEm({ from_email: e.target.value })} placeholder="ventas@cliente.com" />
          <Input id="dc-pass" label="Contraseña de aplicación" type="password" autoComplete="new-password" value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder={acc('email')?.has_secret ? '•••••••• (guardada)' : ''}
            hint={acc('email')?.has_secret ? 'Déjalo vacío para conservar la guardada.' : 'Se guarda cifrada; no se puede volver a ver.'} />
          <Input id="dc-name" label="Nombre del remitente" value={em.from_name} onChange={(e) => setEm({ from_name: e.target.value })} placeholder="Nombre de tu empresa" />
          <Input id="dc-reply" label="Responder a (opcional)" type="email" value={em.reply_to} onChange={(e) => setEm({ reply_to: e.target.value })} />
          <Input id="dc-host" label="Servidor SMTP" value={em.host} onChange={(e) => setEm({ host: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input id="dc-port" label="Puerto" type="number" value={em.port} onChange={(e) => setEm({ port: e.target.value })} />
            <div className="space-y-1">
              <label htmlFor="dc-sec" className="block text-sm" style={{ color: C.text }}>Seguridad</label>
              <select id="dc-sec" className={inputCls} style={inputStyle} value={em.secure} onChange={(e) => setEm({ secure: e.target.value })}>
                <option value="starttls">STARTTLS</option>
                <option value="ssl">SSL</option>
              </select>
            </div>
          </div>
          <Input id="dc-rcpt" label="Destinatarios de los avisos" value={em.recipients} onChange={(e) => setEm({ recipients: e.target.value })}
            placeholder="dueño@cliente.com, gerente@cliente.com" hint="Separados por coma." />
          <Input id="dc-test" label="Enviar la prueba a" type="email" value={testTo.email} onChange={(e) => setTestTo((t) => ({ ...t, email: e.target.value }))} placeholder={list(em.recipients)[0] || em.from_email} />
        </div>
        <Toggle id="dc-em-ins" checked={em.insights} onChange={(v) => setEm({ insights: v })}>Enviar los Avisos de IA por correo</Toggle>
        <Toggle id="dc-em-on" checked={em.enabled} onChange={(v) => setEm({ enabled: v })}>Correo activo (interruptor general de los correos de la plataforma)</Toggle>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => save('email')} disabled={!!busy}
            className="rounded-xl px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ color: C.text, border: `1px solid ${C.line}` }}>
            {busy === 'save-email' ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" onClick={() => test('email')} disabled={!!busy}
            className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ backgroundColor: '#DC1F26' }}>
            <FaPaperPlane aria-hidden /> {busy === 'test-email' ? 'Enviando prueba…' : 'Guardar y enviar prueba'}
          </button>
        </div>
      </section>

      <section className="rounded-2xl p-5 space-y-4" style={card} aria-labelledby="dc-wa">
        <header className="flex flex-wrap items-center gap-2">
          <FaWhatsapp style={{ color: '#4ADE80' }} aria-hidden />
          <h4 id="dc-wa" className="text-sm font-semibold flex-1" style={{ color: C.text }}>WhatsApp (plantilla aprobada)</h4>
          <Status acc={acc('whatsapp')} />
        </header>
        {!state?.whatsapp_ready ? (
          <p className="text-sm" style={{ color: C.muted }}>El proyecto necesita su Phone Number ID y token (paso Plataforma).</p>
        ) : (
          <>
            {acc('whatsapp')?.status === 'error' && <p className="text-xs" style={{ color: '#F87171' }}>{acc('whatsapp').last_error}</p>}
            <p className="text-xs" style={{ color: C.faint }}>La plantilla debe tener dos variables: {'{{1}}'} título y {'{{2}}'} detalle del aviso.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input id="dc-tpl" label="Nombre de la plantilla" value={wa.template_name} onChange={(e) => setWa({ template_name: e.target.value })} placeholder="aviso_ia" />
              <Input id="dc-lang" label="Idioma" value={wa.template_lang} onChange={(e) => setWa({ template_lang: e.target.value })} placeholder="es_MX" />
              <Input id="dc-wa-rcpt" label="Números para los avisos" value={wa.recipients} onChange={(e) => setWa({ recipients: e.target.value })}
                placeholder="5215512345678" hint="Con código de país, separados por coma." />
              <Input id="dc-wa-test" label="Enviar la prueba al número" value={testTo.whatsapp} onChange={(e) => setTestTo((t) => ({ ...t, whatsapp: e.target.value }))} />
            </div>
            <Toggle id="dc-wa-ins" checked={wa.insights} onChange={(v) => setWa({ insights: v })}>Enviar los Avisos de IA por WhatsApp</Toggle>
            <Toggle id="dc-wa-on" checked={wa.enabled} onChange={(v) => setWa({ enabled: v })}>WhatsApp activo (interruptor general)</Toggle>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => save('whatsapp')} disabled={!!busy}
                className="rounded-xl px-4 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ color: C.text, border: `1px solid ${C.line}` }}>
                {busy === 'save-whatsapp' ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" onClick={() => test('whatsapp')} disabled={!!busy}
                className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ backgroundColor: '#DC1F26' }}>
                <FaPaperPlane aria-hidden /> {busy === 'test-whatsapp' ? 'Enviando prueba…' : 'Guardar y enviar prueba'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
